import React from "react";
import { Link } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { orange } from "@material-ui/core/colors";

import { Button, DateField, NumberField, SelectField } from "react-admin";

import {
  DAMAGED_COLOR,
  DamagedIcon,
  ExceptionIcon,
  TimelineIcon,
} from "../../../components/common/iconography";
import { LowInventoryIcon } from "../../../components/common/LowInventoryIcon";
import { PhotoViewer } from "../../../components/common/PhotoViewer";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

import { isAuthorized } from "../../../utils/appUtils";
import {
  AclType,
  InventoryStatus,
  InventoryStatuses,
} from "../../../utils/constants";
import { isBulkLocation } from "../../../utils/locationUtils";

import { InventoryStatusIcon } from "../../../managerApp/resources/inventory/InventoryStatusIcon";
import { OutboundStatusIcon } from "../outboundShipments/OutboundStatusIcon";
import { ValidateRequiredIcon } from "./ValidateRequiredIcon";
import { CopyToClipboardButton } from "../../../components/common/CopyToClipboardButton";

const useAsideStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "18em",
      minWidth: "18em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  centerAlign: {
    textAlign: "center",
  },
  status: {
    marginTop: theme.spacing(0.3),
  },
  tableIcon: {
    marginTop: theme.spacing(0.7),
  },
}));

export const InventoryAside = ({ backPath, record, exceptions, ...props }) => {
  const classes = useAsideStyles();

  // <Edit>/<Show> clone the aside with their own record prop, which is
  // undefined until the controller resolves it. Every read below assumes a
  // record, so the guard has to come before the first one.
  if (!record) return null;

  const linkBackPath = backPath
    ? [...backPath, props.location.pathname]
    : [props.location.pathname];
  const isBulk = record.inventoryItems?.items?.some((item) =>
    isBulkLocation(item?.location),
  );

  // originOfDamage lives on the inventoryItem, never on the inventory: the
  // parent only rolls up the flag, the items say who owes for the units. The
  // grids name the origin in their tooltip, and the aside is where a manager
  // reads the exception detail, so it has to name it too.
  const damageOrigins = [
    ...new Set(
      (record.inventoryItems?.items || [])
        .filter((item) => item.isDamaged && item.originOfDamage)
        .map((item) => item.originOfDamage),
    ),
  ];
  const damageLabel = damageOrigins.length
    ? `Damaged by ${damageOrigins.join(", ")}`
    : "Damaged";

  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid container direction="column" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Status:
            </Typography>
            <Grid container direction="row">
              <Grid item xs={2}>
                <InventoryStatusIcon inventory={record} />
              </Grid>
              <Grid item xs={10} className={classes.status}>
                <SelectField
                  source="status"
                  record={record}
                  choices={InventoryStatuses}
                />
              </Grid>
            </Grid>
            {record.status === InventoryStatus.STORED &&
              record.product?.lowInventoryAlert && (
                <Grid container direction="row">
                  <Grid item xs={2}>
                    <LowInventoryIcon record={record} />
                  </Grid>
                  <Grid item xs={10} className={classes.status}>
                    Low Inventory
                  </Grid>
                </Grid>
              )}
            {(exceptions.hasException || exceptions.isDamaged) && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Exceptions
                </Typography>
              </Grid>
            )}
            {exceptions.hasException && exceptions.overShipment && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.overShipmentNote
                        ? exceptions.overShipmentNote
                        : `Overshipment`
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">
                    Overshipment: {exceptions.overShipmentQuantity}
                  </Typography>
                </Grid>
              </Grid>
            )}
            {exceptions.hasException && exceptions.underShipment && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.underShipmentNote
                        ? exceptions.underShipmentNote
                        : `Undershipment`
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">
                    Undershipment: {exceptions.underShipmentQuantity}
                  </Typography>
                </Grid>
              </Grid>
            )}
            {/*
              Damage stands on its own, without the hasException roll-up. The
              flag lives on the inventoryItem and is set the moment a damaged
              row is created or relocated, while hasException is only
              recalculated on the inventory by the crew inbound save. Gating on
              both hid the whole block for a record the grids were already
              painting red -- the reader saw a red row and an aside claiming
              nothing was wrong.
            */}
            {exceptions.isDamaged && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip title={damageLabel} placement="left" arrow>
                    <DamagedIcon style={{ color: DAMAGED_COLOR }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">{damageLabel}</Typography>
                </Grid>
              </Grid>
            )}
            {exceptions.isRecalled && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.recallNote ? exceptions.recallNote : "Recalled"
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">Recalled</Typography>
                </Grid>
              </Grid>
            )}
            {exceptions.hasException && exceptions.otherException && (
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip
                    title={
                      exceptions.otherExceptionNote
                        ? exceptions.otherExceptionNote
                        : "Other Exception"
                    }
                    placement="left"
                    arrow
                  >
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.status}>
                  <Typography variant="body2">Other Exception</Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
          {record.account && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Account:
              </Typography>
              <br />
              {isAuthorized(AclType.ACCOUNTS) ? (
                <Link
                  to={{
                    pathname: `/accounts/${record.accountId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {accountNameRenderer(record.account)}
                  </Typography>
                </Link>
              ) : (
                <Link
                  to={{
                    pathname: `/accounts/${record.accountId}/show`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {record.account.businessName}
                  </Typography>
                </Link>
              )}
            </Grid>
          )}
          {record.productId && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Product:
              </Typography>
              <br />
              <Box display="flex" alignItems="center">
                <Link
                  to={{
                    pathname: `/products/${record.productId}`,
                    state: { account: record.account, backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {record.productCode}
                  </Typography>
                </Link>
                <Box ml={1}>
                  <CopyToClipboardButton text={record.productCode} />
                </Box>
              </Box>
            </Grid>
          )}
          {record.containerNumber && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Container #:
              </Typography>
              <br />
              <Typography variant="body2">{record.containerNumber}</Typography>
            </Grid>
          )}
          {record.receivedAt && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Date Received:
              </Typography>
              <br />
              <DateField source="receivedAt" record={record} />
            </Grid>
          )}
          <Grid item xs={12}>
            {!isBulk && (
              <>
                <Typography variant="overline" color="textSecondary">
                  Inbound Shipment:
                </Typography>
                <br />
                <Link
                  to={{
                    pathname: `/inboundShipments/${record.inboundShipmentId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    PO no. {record.poNumber}
                  </Typography>
                </Link>
              </>
            )}
            {record.status === InventoryStatus.STORED ||
            record.status === InventoryStatus.SHIPPED ? (
              <Table size="small" padding="none">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption"> </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">Advised</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">Received</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <ValidateRequiredIcon
                        record={record}
                        classes={classes.tableIcon}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberField source="quantityAdvised" record={record} />
                    </TableCell>
                    <TableCell>
                      <NumberField source="quantityReceived" record={record} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : null}
          </Grid>
          {record.shipmentLineItems?.items &&
            record.shipmentLineItems.items.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Outbound Shipments:
                </Typography>
                <br />
                <Table size="small" padding="none">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="caption"> </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">Quantity</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">PO No.</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record.shipmentLineItems.items.map((itm) => (
                      <TableRow>
                        <TableCell>
                          <OutboundStatusIcon record={itm} {...props} />
                        </TableCell>
                        <TableCell>
                          <NumberField
                            source="totalQuantityToPick"
                            record={itm}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            to={{
                              pathname: `/outboundShipments/${itm.outboundShipmentId}`,
                              state: { backPath: linkBackPath },
                            }}
                          >
                            <Typography variant="body2" color="primary">
                              {itm.poNumber}
                            </Typography>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          <Grid item xs={12} className={classes.centerAlign}>
            <Link to={`/search/timeline/${record.id}`}>
              <Button label="View Timeline" color="default">
                <TimelineIcon fontSize="small" />
              </Button>
            </Link>
          </Grid>
          <PhotoViewer photos={record.photos?.items} />
        </Grid>
      </CardContent>
    </Card>
  );
};
